import React, { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "lift-ui";
import { Loader2, GripVertical } from "lucide-react";
import { SortableCardList } from "@/components/sortable-card-list";
import { Station, StationWithChildren, updateStationSortOrder } from "../../api";
import StationCardPreview from "../StationForm/StationCardPreview";

interface ReorderStationsDialogProps {
  open: boolean;
  onClose: () => void;
  stations: StationWithChildren[];
  onReorderComplete?: () => void;
}

interface StationWithId extends Station {
  id: string;
}

// Helper function to flatten nested station structure (parent + children)
const flattenStations = (stations: StationWithChildren[]): StationWithChildren[] => {
  const result: StationWithChildren[] = [];

  const flatten = (station: StationWithChildren) => {
    // Add the current station
    result.push(station);

    // Recursively flatten children if they exist
    if (station.children && station.children.length > 0) {
      station.children.forEach((child: StationWithChildren) => flatten(child));
    }
  };

  stations.forEach(station => flatten(station));
  return result;
};

const ReorderStationsDialog: React.FC<ReorderStationsDialogProps> = ({
  open,
  onClose,
  stations,
  onReorderComplete,
}) => {
  const [sortedStations, setSortedStations] = useState<StationWithId[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const dropdownOpenRef = useRef(false);

  // Initialize sorted stations from props
  useEffect(() => {
    if (open && stations.length > 0) {
      // Flatten nested structure to get all stations (parent + children)
      const allStations = flattenStations(stations);

      // Filter only active stations
      const activeStations = allStations.filter((station) => station.is_active);

      // Map to include id field for SortableCardList
      const stationsWithId: StationWithId[] = activeStations.map((station) => ({
        ...station,
        id: station.buddy_id,
      }));

      // Sort by existing sort_order if available
      const sorted = [...stationsWithId].sort((a, b) => {
        const orderA = a.sort_order;
        const orderB = b.sort_order;

        if (orderA !== undefined && orderB !== undefined) {
          return orderA - orderB;
        }
        if (orderA !== undefined) return -1;
        if (orderB !== undefined) return 1;
        return 0;
      });

      setSortedStations(sorted);
      setHasChanges(false);
    }
  }, [open, stations]);

  const handleReorder = (ids: string[]) => {
    const newOrder = ids.map((id) => sortedStations.find((s) => s.id === id)!);
    setSortedStations(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare the sort order items with 0-based indices
      const sortOrderItems = sortedStations.map((station, index) => ({
        id: station.buddy_id,
        sort_order: index,
      }));

      // Call the API to update sort order
      await updateStationSortOrder(sortOrderItems);

      setHasChanges(false);

      // Notify parent to refresh data
      if (onReorderComplete) {
        onReorderComplete();
      }

      // Close the dialog
      onClose();
    } catch (error) {
      console.error("Error saving station order:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setHasChanges(false);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) handleCancel();
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col"
        onPointerDownOutside={(e) => {
          if (dropdownOpenRef.current) {
            e.preventDefault();
            dropdownOpenRef.current = false;
          }
        }}
      >
        <SheetHeader>
          <SheetTitle>
            <div className="flex items-center gap-2">
              <GripVertical className="h-5 w-5" />
              <span>Reorder Active Stations</span>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600">
              Drag and drop cards to reorder active stations. Changes will be saved when you click "Save Order".
            </p>
          </div>

          {sortedStations.length === 0 ? (
            <div className="flex justify-center items-center min-h-[300px]">
              <p className="text-gray-600">
                No active stations available
              </p>
            </div>
          ) : (
            <div className="min-h-[500px] py-6 px-4">
              <SortableCardList
                items={sortedStations}
                renderCard={(station, index) => (
                  <StationReorderCard
                    station={station}
                    order={index + 1}
                    onDropdownOpenChange={(open) => {
                      dropdownOpenRef.current = open;
                    }}
                  />
                )}
                onReorder={handleReorder}
                disabled={saving}
                gridCols={2}
              />
            </div>
          )}
        </div>

        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving || sortedStations.length === 0}
            variant="default"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Order"
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

function StationReorderCard({
  station,
  order,
  onDropdownOpenChange,
}: {
  station: StationWithId;
  order: number;
  onDropdownOpenChange?: (open: boolean) => void;
}) {
  // Extract data for StationCardPreview using all-buddies API structure
  const starters = (station.meta_config?.base_prompts as any[] || [])
    .map((starter: any) => starter.text)
    .filter((text: string) => text && text.trim());

  const previewData = {
    id: station.buddy_id,
    name: station.name,
    icon: (station.meta_config?.avatar as string) || (station.meta_config?.iconSrc as string) || "",
    buddyType: (station.meta_config?.category as string) || "assistant",
    description: (station.meta_config?.description_brief as string) || station.description || "",
    badgeColor: (station.meta_config?.badgeColor as string) || "bg-blue-100",
    iconBgColor: (station.meta_config?.iconBgColor as string) || "bg-gray-100",
    textColor: (station.meta_config?.textColor as string) || "text-gray-900",
    starters: starters,
    isPreheated: false,
    isWIP: false,
    customColor: (station.meta_config?.color as string) || "",
    customGptUrl: (station.meta_config?.custom_gpt as string) || "",
  };

  return (
    <div className="relative w-full max-w-[320px] mx-auto scale-100 transition-all duration-300 hover:scale-[1.01] hover:z-[5]">
      {/* Modern Order Badge */}
      <div className="absolute -top-1.5 -left-1.5 z-[15]">
        <div className="order-badge bg-primary text-primary-foreground font-bold text-xs px-2.5 py-1.5 rounded-xl shadow-md border-2 border-white min-w-[32px] text-center transition-all duration-300">
          #{order}
        </div>
      </div>

      {/* Modern Drag Handle */}
      <div className="drag-handle absolute -top-1.5 -right-1.5 z-[15] bg-white rounded-xl h-9 w-9 inline-flex items-center justify-center shadow-sm border border-gray-100 cursor-grab transition-all duration-300 hover:bg-gray-50 hover:shadow-md active:cursor-grabbing active:scale-95">
        <GripVertical className="text-slate-500 h-5 w-5 transition-all duration-300" />
      </div>

      {/* Station Card Preview */}
      <StationCardPreview {...previewData} onDropdownOpenChange={onDropdownOpenChange} />
    </div>
  );
}

export default ReorderStationsDialog;
